import { useEffect, useMemo, useState } from 'react';
import { Modal } from '@/components/ui/Modal/Modal';
import { Input } from '@/components/ui/Input/Input';
import { Button } from '@/components/ui/Button/Button';
import { SelectDropdown, type SelectDropdownOption } from '@/components/ui/SelectDropdown/SelectDropdown';
import { api, type ReaderTag } from '@/api/client';
import { useToast } from '@/contexts/ToastContext';
import {
  READER_TAG_CANONICAL_GROUP_ORDER,
  READER_TAG_FALLBACK_GROUP,
  MUSIC_TAG_CANONICAL_GROUP_ORDER,
  READER_TAG_PALETTE_HEX,
  READER_TAG_PALETTE_HINTS,
} from '@/lib/readerTagCatalogGrouping';
import clsx from 'clsx';
import './ReaderTagCreateDialog.css';

const CUSTOM_GROUP = '__custom__';
const HEX_RE = /^#[0-9A-Fa-f]{6}$/;
const DEFAULT_TAG_COLOR = '#42a5f5';

function normalizeHex(c: string): string {
  const t = c.trim().toLowerCase();
  return HEX_RE.test(t) ? t : '';
}

function pickPaletteOrDefault(raw: string): string {
  const n = normalizeHex(raw);
  if (n && READER_TAG_PALETTE_HEX.includes(n)) return n;
  return DEFAULT_TAG_COLOR;
}

export interface ReaderTagCreateDialogProps {
  open: boolean;
  onClose: () => void;
  /** Режим создания (не задан `editTag`). */
  onCreated?: (tagId: string) => void;
  /** Режим редактирования — предзаполнение и PATCH. */
  editTag?: ReaderTag | null;
  onUpdated?: (tagId: string) => void;
  /** Справочник: чтецы или фонотека (группы и API). */
  catalogKind?: 'reader' | 'music';
}

const GROUP_OPTIONS_READER: SelectDropdownOption[] = [
  ...READER_TAG_CANONICAL_GROUP_ORDER.map((g) => ({ value: g, label: g })),
  { value: READER_TAG_FALLBACK_GROUP, label: READER_TAG_FALLBACK_GROUP },
  { value: CUSTOM_GROUP, label: 'Своя группа…' },
];

const GROUP_OPTIONS_MUSIC: SelectDropdownOption[] = [
  ...MUSIC_TAG_CANONICAL_GROUP_ORDER.map((g) => ({ value: g, label: g })),
  { value: READER_TAG_FALLBACK_GROUP, label: READER_TAG_FALLBACK_GROUP },
  { value: CUSTOM_GROUP, label: 'Своя группа…' },
];

function resolveGroupFields(
  tagGroup: string | undefined,
  groupOptions: SelectDropdownOption[]
): { select: string; custom: string } {
  const g = (tagGroup ?? '').trim() || READER_TAG_FALLBACK_GROUP;
  const presetValues = new Set(groupOptions.map((o) => o.value).filter((v) => v !== CUSTOM_GROUP));
  if (presetValues.has(g)) return { select: g, custom: '' };
  return { select: CUSTOM_GROUP, custom: g };
}

export function ReaderTagCreateDialog({
  open,
  onClose,
  onCreated,
  editTag,
  onUpdated,
  catalogKind = 'reader',
}: ReaderTagCreateDialogProps) {
  const { showError, showSuccess } = useToast();
  const canonicalOrder = useMemo(
    () => (catalogKind === 'music' ? MUSIC_TAG_CANONICAL_GROUP_ORDER : READER_TAG_CANONICAL_GROUP_ORDER),
    [catalogKind]
  );
  const groupOptions = useMemo(
    () => (catalogKind === 'music' ? GROUP_OPTIONS_MUSIC : GROUP_OPTIONS_READER),
    [catalogKind]
  );
  const [name, setName] = useState('');
  const [color, setColor] = useState(DEFAULT_TAG_COLOR);
  const [groupSelect, setGroupSelect] = useState<string>(canonicalOrder[0] ?? READER_TAG_FALLBACK_GROUP);
  const [customGroupName, setCustomGroupName] = useState('');
  const [saving, setSaving] = useState(false);

  const isEdit = Boolean(editTag);

  useEffect(() => {
    if (!open) return;
    if (editTag) {
      setName(editTag.name);
      setColor(pickPaletteOrDefault(editTag.color));
      const { select, custom } = resolveGroupFields(editTag.tag_group, groupOptions);
      setGroupSelect(select);
      setCustomGroupName(custom);
    } else {
      setName('');
      setColor(DEFAULT_TAG_COLOR);
      setGroupSelect(canonicalOrder[0] ?? READER_TAG_FALLBACK_GROUP);
      setCustomGroupName('');
    }
  }, [open, editTag, groupOptions, canonicalOrder]);

  const resolvedTagGroup = useMemo(() => {
    if (groupSelect === CUSTOM_GROUP) {
      const c = customGroupName.trim();
      return c || READER_TAG_FALLBACK_GROUP;
    }
    return groupSelect;
  }, [groupSelect, customGroupName]);

  const handleClose = () => {
    if (!saving) {
      setName('');
      setColor(DEFAULT_TAG_COLOR);
      setGroupSelect(canonicalOrder[0] ?? READER_TAG_FALLBACK_GROUP);
      setCustomGroupName('');
      onClose();
    }
  };

  const tagApi = catalogKind === 'music' ? api.musicTags : api.readerTags;

  const submit = async () => {
    const n = name.trim();
    if (!n) {
      showError('Введите название тега');
      return;
    }
    if (groupSelect === CUSTOM_GROUP && !customGroupName.trim()) {
      showError('Укажите название новой группы или выберите из списка');
      return;
    }
    const c = normalizeHex(color);
    if (!c || !READER_TAG_PALETTE_HEX.includes(c)) {
      showError('Выберите цвет из палитры');
      return;
    }
    setSaving(true);
    try {
      if (isEdit && editTag) {
        const row = await tagApi.update(editTag.id, {
          name: n,
          color: c,
          tag_group: resolvedTagGroup,
        });
        showSuccess('Изменения сохранены');
        onUpdated?.(row.id);
      } else {
        const row = await tagApi.create({
          name: n,
          color: c,
          tag_group: resolvedTagGroup,
        });
        showSuccess('Тег добавлен');
        onCreated?.(row.id);
      }
      setName('');
      setColor(DEFAULT_TAG_COLOR);
      setGroupSelect(canonicalOrder[0] ?? READER_TAG_FALLBACK_GROUP);
      setCustomGroupName('');
      onClose();
    } catch (e) {
      showError(
        e instanceof Error
          ? e.message
          : isEdit
            ? 'Не удалось сохранить тег'
            : 'Не удалось создать тег'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={isEdit ? 'Редактировать тег' : 'Новый тег'}
      headerVariant="island"
      size="sm"
    >
      <div className="reader-tag-create-dialog">
        <Input
          placeholder="Название"
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={saving}
          maxLength={128}
          fullWidth
          autoComplete="off"
          aria-label="Название тега"
        />
        <SelectDropdown
          className="reader-tag-create-dialog__group-dropdown"
          options={groupOptions}
          value={groupSelect}
          onChange={setGroupSelect}
          placeholder="Группа"
          disabled={saving}
          fullWidth
        />
        {groupSelect === CUSTOM_GROUP ? (
          <Input
            placeholder="Название группы"
            value={customGroupName}
            onChange={(e) => setCustomGroupName(e.target.value)}
            disabled={saving}
            maxLength={128}
            fullWidth
            autoComplete="off"
            aria-label="Новая группа"
          />
        ) : null}
        <div className="reader-tag-create-dialog__palette">
          <p className="reader-tag-create-dialog__palette-label">Цвет из палитры</p>
          <div className="reader-tag-create-dialog__palette-grid" role="group" aria-label="Цвет тега">
            {READER_TAG_PALETTE_HEX.map((hex) => (
              <button
                key={hex}
                type="button"
                className={clsx('reader-tag-create-dialog__swatch', hex === color && 'reader-tag-create-dialog__swatch_selected')}
                disabled={saving}
                onClick={() => setColor(hex)}
                title={READER_TAG_PALETTE_HINTS[hex] ?? hex}
                aria-label={READER_TAG_PALETTE_HINTS[hex] ?? hex}
                aria-pressed={hex === color}
              >
                <span className="reader-tag-create-dialog__swatch-inner" style={{ backgroundColor: hex }} />
              </button>
            ))}
          </div>
        </div>
        <div className="form__footer form__footer_modal reader-tag-create-dialog__footer">
          <Button variant="outlined" type="button" size="m" fullWidth onClick={handleClose} disabled={saving}>
            Отмена
          </Button>
          <Button type="button" size="m" fullWidth onClick={() => void submit()} disabled={saving}>
            {saving ? 'Сохранение…' : isEdit ? 'Сохранить' : 'Создать'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
